import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameYear,
  isValid,
  setMonth,
  setYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { de, enUS as en, it } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import axios from "../../../config/axios";
import styles from "./OnlineReservationDateRangePicker.module.scss";

const WEEK_OPTIONS = { weekStartsOn: 1 };
const MONTH_COUNT = 2;

export const dateKey = (date) => format(date, "yyyy-MM-dd");

export const calendarDays = (month) => {
  const firstDay = startOfWeek(startOfMonth(month), WEEK_OPTIONS);

  return eachDayOfInterval({
    start: firstDay,
    end: addDays(firstDay, 41),
  });
};

export const localeForLanguage = (language) => {
  if (language && language.startsWith("it")) return it;
  if (language && language.startsWith("en")) return en;

  return de;
};

const normalizeDate = (value) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? startOfDay(date) : null;
};

const OnlineReservationsDateRangePicker = ({
  from,
  to,
  setFieldValue,
  onChange = null,
  minimumStays = [],
  reservationYears = [],
}) => {
  const { t, i18n } = useTranslation("reservations");
  const today = useMemo(() => startOfDay(new Date()), []);
  const initialFrom = normalizeDate(from);
  const initialTo = normalizeDate(to);
  const [selectedFrom, setSelectedFrom] = useState(initialFrom);
  const [selectedTo, setSelectedTo] = useState(
    initialFrom && initialTo && isAfter(initialTo, initialFrom)
      ? initialTo
      : null
  );
  const [anchorMonth, setAnchorMonth] = useState(
    startOfMonth(initialFrom || today)
  );
  const [focusedDate, setFocusedDate] = useState(initialFrom || today);
  const [availabilityMonths, setAvailabilityMonths] = useState({});
  const [loadingMonths, setLoadingMonths] = useState({});
  const [errorMonths, setErrorMonths] = useState({});
  const requestedMonths = useRef(new Set());
  const dayRefs = useRef({});
  const mounted = useRef(true);
  const locale = localeForLanguage(i18n.language);

  const selectionPhase = selectedFrom
    ? selectedTo
      ? "complete"
      : "departure"
    : "arrival";

  const availabilities = useMemo(
    () =>
      Object.values(availabilityMonths).reduce(
        (result, month) => ({ ...result, ...month }),
        {}
      ),
    [availabilityMonths]
  );

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    const nextFrom = normalizeDate(from);
    const nextTo = normalizeDate(to);
    setSelectedFrom(nextFrom);
    setSelectedTo(
      nextFrom && nextTo && isAfter(nextTo, nextFrom) ? nextTo : null
    );
  }, [from, to]);

  const fetchMonth = useCallback((month) => {
    const key = dateKey(startOfMonth(month));
    if (requestedMonths.current.has(key)) return;

    requestedMonths.current.add(key);
    setLoadingMonths((current) => ({ ...current, [key]: true }));
    setErrorMonths((current) => ({ ...current, [key]: false }));

    axios
      .get("online_reservations/availabilities.json", {
        params: { from: key },
      })
      .then((response) => {
        if (!mounted.current) return;
        setAvailabilityMonths((current) => ({
          ...current,
          [key]: response.data || {},
        }));
      })
      .catch(() => {
        requestedMonths.current.delete(key);
        if (!mounted.current) return;
        setErrorMonths((current) => ({ ...current, [key]: true }));
      })
      .finally(() => {
        if (!mounted.current) return;
        setLoadingMonths((current) => ({ ...current, [key]: false }));
      });
  }, []);

  useEffect(() => {
    fetchMonth(anchorMonth);
    fetchMonth(addMonths(anchorMonth, 1));
  }, [anchorMonth, fetchMonth]);

  useEffect(() => {
    const node = dayRefs.current[dateKey(focusedDate)];
    if (node && document.activeElement?.dataset?.date) node.focus();
  }, [anchorMonth, focusedDate]);

  const departureKeys = useMemo(() => {
    if (!selectedFrom) return new Set();

    return new Set(availabilities[dateKey(selectedFrom)]?.departures || []);
  }, [availabilities, selectedFrom]);

  const clearSelection = useCallback(() => {
    setSelectedFrom(null);
    setSelectedTo(null);
    setFieldValue("from", null);
    setFieldValue("to", null);
    if (onChange) onChange(null, null);
  }, [onChange, setFieldValue]);

  const startSelection = useCallback(
    (day, clearCompletedSelection = false) => {
      if (clearCompletedSelection && onChange) onChange(null, null);
      setSelectedFrom(day);
      setSelectedTo(null);
      setFieldValue("from", day);
      setFieldValue("to", null);
    },
    [onChange, setFieldValue]
  );

  const completeSelection = useCallback(
    (day) => {
      setSelectedTo(day);
      setFieldValue("to", day);
      if (onChange) onChange(selectedFrom, day);
    },
    [onChange, selectedFrom, setFieldValue]
  );

  const dayState = useCallback(
    (day) => {
      const key = dateKey(day);
      const arrivalPossible = Boolean(availabilities[key]);
      const past = isBefore(day, today);

      if (past) return { name: "unavailable", interactive: false };

      if (selectionPhase === "complete") {
        const inRange =
          !isBefore(day, selectedFrom) && !isAfter(day, selectedTo);

        if (inRange) {
          return {
            name: "selected",
            interactive: true,
            rangeStart: isSameDay(day, selectedFrom),
            rangeEnd: isSameDay(day, selectedTo),
          };
        }

        return arrivalPossible
          ? { name: "arrival", interactive: true }
          : { name: "unavailable", interactive: false };
      }

      if (selectionPhase === "departure") {
        if (isSameDay(day, selectedFrom)) {
          return { name: "selectedStart", interactive: true };
        }

        if (isAfter(day, selectedFrom)) {
          return departureKeys.has(key)
            ? { name: "departure", interactive: true }
            : { name: "invalidDeparture", interactive: false };
        }

        return arrivalPossible
          ? { name: "arrival", interactive: true }
          : { name: "unavailable", interactive: false };
      }

      return arrivalPossible
        ? { name: "arrival", interactive: true }
        : { name: "unavailable", interactive: false };
    },
    [
      availabilities,
      departureKeys,
      selectedFrom,
      selectedTo,
      selectionPhase,
      today,
    ]
  );

  const selectDay = useCallback(
    (day) => {
      const state = dayState(day);
      setFocusedDate(day);

      if (!state.interactive) return;

      if (selectedFrom && isSameDay(day, selectedFrom)) {
        clearSelection();
        return;
      }

      if (state.name === "departure") {
        completeSelection(day);
        return;
      }

      startSelection(day, selectionPhase === "complete");
    },
    [
      clearSelection,
      completeSelection,
      dayState,
      selectedFrom,
      selectionPhase,
      startSelection,
    ]
  );

  const moveFocus = useCallback(
    (day) => {
      const targetMonth = startOfMonth(day);
      const lastVisibleMonth = addMonths(anchorMonth, MONTH_COUNT - 1);

      if (isBefore(targetMonth, anchorMonth)) {
        setAnchorMonth(targetMonth);
      } else if (isAfter(targetMonth, lastVisibleMonth)) {
        setAnchorMonth(subMonths(targetMonth, MONTH_COUNT - 1));
      }

      setFocusedDate(day);
    },
    [anchorMonth]
  );

  const showMonth = useCallback(
    (month) => {
      const nextAnchor = startOfMonth(month);
      const lastVisibleMonth = addMonths(nextAnchor, MONTH_COUNT - 1);

      setAnchorMonth(nextAnchor);
      setFocusedDate((current) =>
        isBefore(current, nextAnchor) ||
        isAfter(current, endOfMonth(lastVisibleMonth))
          ? isSameMonth(nextAnchor, today)
            ? today
            : nextAnchor
          : current
      );
    },
    [today]
  );

  const handleDayKeyDown = useCallback(
    (event, day) => {
      let nextDay = null;

      switch (event.key) {
        case "ArrowLeft":
          nextDay = addDays(day, -1);
          break;
        case "ArrowRight":
          nextDay = addDays(day, 1);
          break;
        case "ArrowUp":
          nextDay = addDays(day, -7);
          break;
        case "ArrowDown":
          nextDay = addDays(day, 7);
          break;
        case "Home":
          nextDay = startOfWeek(day, WEEK_OPTIONS);
          break;
        case "End":
          nextDay = endOfWeek(day, WEEK_OPTIONS);
          break;
        case "PageUp":
          nextDay = addMonths(day, -1);
          break;
        case "PageDown":
          nextDay = addMonths(day, 1);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          selectDay(day);
          return;
        default:
          return;
      }

      event.preventDefault();
      if (!isBefore(nextDay, today)) moveFocus(nextDay);
    },
    [moveFocus, selectDay, today]
  );

  const updatePanelMonth = useCallback(
    (panelIndex, value) => {
      const displayedMonth = addMonths(anchorMonth, panelIndex);
      const requestedMonth = startOfMonth(
        setMonth(displayedMonth, Number(value))
      );
      const nextAnchor =
        panelIndex === 0 ? requestedMonth : subMonths(requestedMonth, 1);

      showMonth(
        isBefore(nextAnchor, startOfMonth(today))
          ? startOfMonth(today)
          : nextAnchor
      );
    },
    [anchorMonth, showMonth, today]
  );

  const updatePanelYear = useCallback(
    (panelIndex, value) => {
      const displayedMonth = addMonths(anchorMonth, panelIndex);
      const requestedMonth = startOfMonth(
        setYear(displayedMonth, Number(value))
      );
      const nextAnchor =
        panelIndex === 0 ? requestedMonth : subMonths(requestedMonth, 1);

      showMonth(
        isBefore(nextAnchor, startOfMonth(today))
          ? startOfMonth(today)
          : nextAnchor
      );
    },
    [anchorMonth, showMonth, today]
  );

  const showPreviousMonth = useCallback(() => {
    if (isAfter(anchorMonth, startOfMonth(today))) {
      showMonth(subMonths(anchorMonth, 1));
    }
  }, [anchorMonth, showMonth, today]);

  const showNextMonth = useCallback(() => {
    showMonth(addMonths(anchorMonth, 1));
  }, [anchorMonth, showMonth]);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index,
        label: format(new Date(2020, index, 1), "LLLL", { locale }),
      })),
    [locale]
  );

  const weekdays = Array.from({ length: 7 }, (_, index) =>
    addDays(startOfWeek(new Date(2020, 0, 6), WEEK_OPTIONS), index)
  );

  const stateLabel = (state) => {
    switch (state.name) {
      case "arrival":
        return t("datePicker.arrivalPossible");
      case "departure":
        return t("datePicker.departurePossible");
      case "invalidDeparture":
        return t("datePicker.departureNotPossible");
      case "selected":
      case "selectedStart":
        return t("datePicker.selected");
      default:
        return t("datePicker.unavailable");
    }
  };

  const staySummary =
    selectionPhase === "complete"
      ? t(
          isSameMonth(selectedFrom, selectedTo)
            ? "datePicker.staySummary"
            : "datePicker.staySummaryCrossMonth",
          isSameMonth(selectedFrom, selectedTo)
            ? {
                from: format(selectedFrom, "d", { locale }),
                to: format(selectedTo, "d", { locale }),
                month: format(selectedFrom, "LLLL", { locale }),
                nights: differenceInCalendarDays(selectedTo, selectedFrom),
              }
            : {
                from: format(selectedFrom, "d", { locale }),
                to: format(selectedTo, "d", { locale }),
                fromMonth: format(
                  selectedFrom,
                  isSameYear(selectedFrom, selectedTo) ? "LLLL" : "LLLL yyyy",
                  { locale }
                ),
                toMonth: format(
                  selectedTo,
                  isSameYear(selectedFrom, selectedTo) ? "LLLL" : "LLLL yyyy",
                  { locale }
                ),
                nights: differenceInCalendarDays(selectedTo, selectedFrom),
              }
        )
      : null;

  const minimumStaySummary = minimumStays
    .map(
      ({ name, min_reservation_days: days }) =>
        `${name} ${t("datePicker.nights", { count: days })}`
    )
    .join(", ");

  const renderMonth = (month, panelIndex) => {
    const monthKey = dateKey(startOfMonth(month));
    const loading = loadingMonths[monthKey];
    const hasError = errorMonths[monthKey];
    const days = calendarDays(month);

    return (
      <section
        className={styles.monthPanel}
        key={monthKey}
        aria-labelledby={`reservation-calendar-${monthKey}`}
      >
        <div className={styles.monthHeader}>
          <div className={styles.monthPicker}>
            <label
              className={styles.visuallyHidden}
              htmlFor={`month-${monthKey}`}
            >
              {t("datePicker.selectMonth")}
            </label>
            <select
              id={`month-${monthKey}`}
              className={styles.monthSelect}
              value={month.getMonth()}
              onChange={(event) =>
                updatePanelMonth(panelIndex, event.target.value)
              }
            >
              {months.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label
              className={styles.visuallyHidden}
              htmlFor={`year-${monthKey}`}
            >
              {t("datePicker.selectYear")}
            </label>
            <select
              id={`year-${monthKey}`}
              className={styles.yearSelect}
              value={month.getFullYear()}
              onChange={(event) =>
                updatePanelYear(panelIndex, event.target.value)
              }
            >
              {reservationYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <h2
            id={`reservation-calendar-${monthKey}`}
            className={styles.visuallyHidden}
          >
            {format(month, "LLLL yyyy", { locale })}
          </h2>
        </div>

        <div
          role="grid"
          aria-label={format(month, "LLLL yyyy", { locale })}
          aria-busy={Boolean(loading)}
        >
          <div className={styles.weekdays} role="row">
            {weekdays.map((weekday) => (
              <div
                className={styles.weekday}
                role="columnheader"
                aria-label={format(weekday, "EEEE", { locale })}
                key={dateKey(weekday)}
              >
                {format(weekday, "EEEEEE", { locale })}
              </div>
            ))}
          </div>
          <div role="rowgroup">
            {Array.from({ length: 6 }, (_, weekIndex) => (
              <div className={styles.week} role="row" key={weekIndex}>
                {days.slice(weekIndex * 7, weekIndex * 7 + 7).map((day) => {
                  if (!isSameMonth(day, month)) {
                    return (
                      <div
                        className={styles.emptyDay}
                        role="gridcell"
                        key={dateKey(day)}
                      />
                    );
                  }

                  const state = dayState(day);
                  const key = dateKey(day);
                  const className = [
                    styles.day,
                    styles[state.name],
                    state.rangeStart ? styles.rangeStart : "",
                    state.rangeEnd ? styles.rangeEnd : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const label = `${format(day, "PPPP", {
                    locale,
                  })}, ${stateLabel(state)}`;

                  return (
                    <div
                      className={styles.dayCell}
                      role="gridcell"
                      aria-selected={
                        state.name === "selected" ||
                        state.name === "selectedStart"
                      }
                      key={key}
                    >
                      <button
                        ref={(node) => {
                          if (node) dayRefs.current[key] = node;
                        }}
                        type="button"
                        className={className}
                        data-date={key}
                        data-state={state.name}
                        tabIndex={isSameDay(day, focusedDate) ? 0 : -1}
                        aria-label={label}
                        aria-disabled={!state.interactive}
                        onClick={() => selectDay(day)}
                        onKeyDown={(event) => handleDayKeyDown(event, day)}
                      >
                        {format(day, "d")}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {loading && (
          <div className={styles.status} role="status">
            <span className={styles.spinner} aria-hidden="true" />
            {t("datePicker.loading")}
          </div>
        )}
      </section>
    );
  };

  return (
    <div>
      <p className={styles.instructions} aria-live="polite">
        {staySummary ? (
          <>
            <span className={styles.staySummary}>{staySummary}</span>
            <span className={styles.changeArrival}>
              {t("datePicker.changeArrival")}
            </span>
          </>
        ) : selectionPhase === "arrival" ? (
          minimumStaySummary ? (
            t("datePicker.chooseArrivalWithMinimumStays", {
              minimumStays: minimumStaySummary,
            })
          ) : (
            t("datePicker.chooseArrival")
          )
        ) : (
          t("datePicker.chooseDeparture")
        )}
      </p>

      <div className={styles.calendarShell}>
        <button
          type="button"
          className={`${styles.navigationButton} ${styles.previousButton}`}
          aria-label={t("datePicker.previousMonth")}
          aria-disabled={!isAfter(anchorMonth, startOfMonth(today))}
          onClick={showPreviousMonth}
        >
          <span aria-hidden="true">&#8249;</span>
        </button>

        <div className={styles.months}>
          {Array.from({ length: MONTH_COUNT }, (_, index) =>
            renderMonth(addMonths(anchorMonth, index), index)
          )}
        </div>

        <button
          type="button"
          className={`${styles.navigationButton} ${styles.nextButton}`}
          aria-label={t("datePicker.nextMonth")}
          onClick={showNextMonth}
        >
          <span aria-hidden="true">&#8250;</span>
        </button>
      </div>

      <div className={styles.legend} aria-label={t("datePicker.legend")}>
        <span>
          <i className={styles.arrivalSwatch} />
          {t("datePicker.arrivalPossible")}
        </span>
        <span>
          <i className={styles.invalidSwatch} />
          {t("datePicker.departureNotPossible")}
        </span>
        <span>
          <i className={styles.selectionSwatch} />
          {t("datePicker.selected")}
        </span>
      </div>
    </div>
  );
};

export default OnlineReservationsDateRangePicker;
